/**
 * Cart Drawer — Shipping Protection
 *
 * Shipping Protection: Adds/removes a product variant from cart
 *   – Auto-added when regular products exist and user hasn't opted out
 *   – Auto-removed when no regular products remain
 *
 * Note: the gift bonus is now a passive "Wrapped with care" row (T7) — it no
 * longer toggles a checkbox or writes the gift_wrap cart attribute.
 */
(function () {

    var SHIPPING_PROTECTION_DISABLED_KEY = 'thekiss_shipping_protection_disabled';

    // Flag to prevent recursive auto-add during our own operations
    var isAutoAdding = false;

    /**
     * Prepare bundled sections for cart operations so cart-drawer HTML updates automatically
     */
    function prepareBundledSections() {
        var sectionsToBundle = [];

        document.dispatchEvent(
            new CustomEvent('cart:prepare-bundled-sections', {
                bubbles: true,
                detail: { sections: sectionsToBundle }
            })
        );

        if (sectionsToBundle.length === 0) {
            var cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
                var section = cartDrawer.closest('.shopify-section');
                if (section && section.id) {
                    sectionsToBundle.push(section.id.replace('shopify-section-', ''));
                }
            }
        }

        return sectionsToBundle;
    }

    // ─── Shipping Protection (cart product) ──────────────────────────────────

    /**
     * Add a single unit of a variant to cart
     */
    async function addToCart(variantId) {
        var sectionsToBundle = prepareBundledSections();
        var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        var res = await fetch(rootUrl + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
                items: [{ id: parseInt(variantId, 10), quantity: 1 }],
                sections: sectionsToBundle.join(',')
            })
        });

        if (!res.ok) {
            var text = await res.text();
            throw new Error('Add failed (' + res.status + '): ' + text);
        }

        return res.json();
    }

    /**
     * Remove a variant from cart by key
     */
    async function removeFromCart(variantId) {
        var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        var cartRes = await fetch(rootUrl + 'cart.js', { headers: { 'Accept': 'application/json' } });
        var cart = await cartRes.json();

        var lineItem = cart.items.find(function (item) {
            return String(item.variant_id) === String(variantId);
        });

        if (!lineItem) {
            console.warn('[Shipping Protection] Line item not found for variant:', variantId);
            return cart;
        }

        var sectionsToBundle = prepareBundledSections();

        var res = await fetch(rootUrl + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
                id: lineItem.key,
                quantity: 0,
                sections: sectionsToBundle.join(',')
            })
        });

        if (!res.ok) {
            var text = await res.text();
            throw new Error('Remove failed (' + res.status + '): ' + text);
        }

        return res.json();
    }

    function isManuallyDisabled(storageKey) {
        try { return localStorage.getItem(storageKey) === 'true'; } catch (e) { return false; }
    }

    function setManuallyDisabled(storageKey, value) {
        try {
            if (value) { localStorage.setItem(storageKey, 'true'); }
            else { localStorage.removeItem(storageKey); }
        } catch (e) { /* ignore */ }
    }

    function isVariantInCart(cart, variantId) {
        if (!cart || !cart.items) return false;
        return cart.items.some(function (item) {
            return String(item.variant_id) === String(variantId);
        });
    }

    /**
     * Returns true if cart has any items that are not shipping protection
     */
    function hasRegularProducts(cart, shippingProtectionVariantId) {
        if (!cart || !cart.items || cart.items.length === 0) return false;
        return cart.items.some(function (item) {
            return !shippingProtectionVariantId ||
                String(item.variant_id) !== String(shippingProtectionVariantId);
        });
    }

    /**
     * Handle shipping protection checkbox toggle (adds/removes product from cart)
     */
    async function handleShippingProtectionToggle(checkbox, variantId) {
        if (!variantId) {
            console.warn('[Shipping Protection] No variant ID found');
            checkbox.checked = !checkbox.checked;
            return;
        }

        console.log('[Shipping Protection] Toggle, checked:', checkbox.checked, 'variantId:', variantId);
        checkbox.disabled = true;

        try {
            var cartResponse;

            if (checkbox.checked) {
                cartResponse = await addToCart(variantId);
                setManuallyDisabled(SHIPPING_PROTECTION_DISABLED_KEY, false);
            } else {
                cartResponse = await removeFromCart(variantId);
                setManuallyDisabled(SHIPPING_PROTECTION_DISABLED_KEY, true);
            }

            document.dispatchEvent(
                new CustomEvent('cart:change', {
                    bubbles: true,
                    detail: {
                        baseEvent: checkbox.checked ? 'shipping-protection:add' : 'shipping-protection:remove',
                        cart: cartResponse
                    }
                })
            );
        } catch (e) {
            console.error('[Shipping Protection] Error:', e);
            checkbox.checked = !checkbox.checked;
        } finally {
            checkbox.disabled = false;
        }
    }

    /**
     * Auto-add/remove shipping protection based on cart contents
     */
    async function autoAddExtras(cart) {
        if (isAutoAdding) return;
        if (!cart || !cart.items || cart.items.length === 0) return;

        var spElement = document.querySelector('shipping-protection');
        if (!spElement) return;

        var spVariantId = spElement.dataset.variantId;
        var hasRegular = hasRegularProducts(cart, spVariantId);

        if (!hasRegular) {
            // No regular products — remove shipping protection if present
            if (spVariantId && isVariantInCart(cart, spVariantId)) {
                console.log('[Auto-remove] Removing shipping protection (no regular products)');
                isAutoAdding = true;
                try {
                    var removed = await removeFromCart(spVariantId);
                    document.dispatchEvent(new CustomEvent('cart:change', {
                        bubbles: true,
                        detail: { baseEvent: 'auto-remove-extras', cart: removed }
                    }));
                } catch (e) {
                    console.error('[Auto-remove] Error:', e);
                } finally {
                    isAutoAdding = false;
                }
            }
            return;
        }

        // Regular products exist — auto-add shipping protection if not present and not opted out
        var spInCart = isVariantInCart(cart, spVariantId);
        var spDisabled = isManuallyDisabled(SHIPPING_PROTECTION_DISABLED_KEY);

        if (spVariantId && !spInCart && !spDisabled) {
            console.log('[Auto-add] Adding shipping protection');
            isAutoAdding = true;
            try {
                var sectionsToBundle = prepareBundledSections();
                var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

                var res = await fetch(rootUrl + 'cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({
                        items: [{ id: parseInt(spVariantId, 10), quantity: 1 }],
                        sections: sectionsToBundle.join(',')
                    })
                });

                if (!res.ok) throw new Error('Auto-add failed: ' + res.status);

                var cartResponse = await res.json();
                document.dispatchEvent(new CustomEvent('cart:change', {
                    bubbles: true,
                    detail: { baseEvent: 'auto-add-extras', cart: cartResponse }
                }));
            } catch (e) {
                console.error('[Auto-add] Error:', e);
            } finally {
                isAutoAdding = false;
            }
        }
    }

    // ─── Event listeners ─────────────────────────────────────────────────────

    /**
     * Single document-level change listener — survives DOM replacement by Section Rendering API
     */
    document.addEventListener('change', function (e) {
        var target = e.target;

        if (target.id === 'shipping-protection-checkbox') {
            var sp = target.closest('shipping-protection');
            handleShippingProtectionToggle(target, sp && sp.dataset.variantId);
            return;
        }
    });

    /**
     * Listen for cart:change to auto-add shipping protection when regular products are added
     */
    document.addEventListener('cart:change', function (event) {
        var baseEvent = event.detail && event.detail.baseEvent;
        var cart = event.detail && event.detail.cart;

        // Skip events we fired ourselves
        if (baseEvent === 'auto-add-extras' ||
            baseEvent === 'auto-remove-extras' ||
            baseEvent === 'shipping-protection:add' ||
            baseEvent === 'shipping-protection:remove') {
            return;
        }

        console.log('[Auto-add] Cart changed, checking shipping protection. Base event:', baseEvent);

        // Small delay to ensure cart-drawer elements are rendered after Section Rendering API update
        setTimeout(function () { autoAddExtras(cart); }, 100);
    });

})();

// ── Checkout button loading state ─────────────────────────────────────────
(function () {
    document.addEventListener('click', function (e) {
        var btn = e.target.closest('.cart-drawer__checkout-btn');
        if (!btn || btn.getAttribute('aria-busy') === 'true') return;
        btn.setAttribute('aria-busy', 'true');
    });

    // Reset on back navigation (bfcache restore)
    window.addEventListener('pageshow', function (e) {
        if (e.persisted) {
            var btn = document.querySelector('.cart-drawer__checkout-btn');
            if (btn) btn.removeAttribute('aria-busy');
        }
    });

     // ── Line Item Loader ──────────────────────────────────────────────────────
    function ensureLoader(lineItem) {
        if (!lineItem.querySelector('.line-item__loader')) {
            lineItem.insertAdjacentHTML(
                'beforeend', 
                '<div class="line-item__loader"><div class="line-item__spinner"></div></div>'
            );
        }
    }

    // Delegated from document → survives the wholesale cart-drawer child replace on
    // cart:change / cart:refresh. Selectors target stable data-hooks first
    // ([data-line-remove], [data-qty-control]) with the AJAX href as a fallback.
    document.addEventListener('click', function (e) {
        const cartDrawer = e.target.closest('#cart-drawer');
        if (!cartDrawer) return;

        const actionBtn = e.target.closest('[data-line-remove], [data-qty-control] a, [data-qty-control] button, a[href*="/cart/change?"]');

        if (actionBtn && !actionBtn.disabled) {
            const lineItem = actionBtn.closest('.line-item');
            if (lineItem) {
                ensureLoader(lineItem);
                lineItem.classList.add('is-loading');
            }
        }
    });

    document.addEventListener('change', function (e) {
        const cartDrawer = e.target.closest('#cart-drawer');
        if (!cartDrawer) return;

        const qtyInput = e.target.closest('[data-qty-control] input, quantity-selector input');
        if (qtyInput) {
            const lineItem = qtyInput.closest('.line-item');
            if (lineItem) {
                ensureLoader(lineItem);
                lineItem.classList.add('is-loading');
            }
        }
    });
})();

// ── Sticky Cart Checkout ──────────────────────────────────────────────────────
(function () {
    // The express-checkout buttons live inline in the footer on desktop, but on
    // mobile they're relocated into #cart-express-sheet (a sibling of <cart-drawer>)
    // so the bottom-sheet overlay isn't clipped by the drawer's own open/close
    // animation, which leaves a `transform` on its shadow-part footer/content and
    // turns that into a containing block for any position:fixed descendant.
    let expressMediaQuery;
    let currentExpressGroup;

    function placeExpressGroup(isMobile) {
        const sheet = document.querySelector('[data-express-sheet]');
        const anchor = document.querySelector('[data-express-anchor]');
        if (!currentExpressGroup || !sheet || !anchor) return;
        // Node.after()/appendChild() detach-and-reattach even when the target position is
        // unchanged, which forces shopify-accelerated-checkout-cart through a needless
        // disconnect/reconnect -- and its connectedCallback appends rather than replaces its
        // button markup, so every redundant move duplicates the express-checkout buttons.
        if (isMobile) {
            if (currentExpressGroup.parentElement !== sheet) {
                sheet.appendChild(currentExpressGroup);
            }
        } else if (currentExpressGroup.previousElementSibling !== anchor) {
            anchor.after(currentExpressGroup);
        }
    }

    document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-express-toggle]');
        if (toggle) {
            const open = document.body.classList.toggle('is-express-open');
            toggle.setAttribute('aria-expanded', String(open));
            if (open) {
                const total = document.querySelector('.cart-drawer__total-price');
                const target = document.querySelector('[data-express-sheet-total]');
                if (total && target) target.textContent = total.textContent;
            }
            return;
        }
        // event.target === sheet (not a descendant) means the click landed on the sheet's own
        // padding area, which is where the ::before drag-handle bar renders -- treat that as a
        // dismiss too, matching common bottom-sheet behavior.
        if (
            event.target.closest('[data-express-backdrop]')
            || event.target.closest('[data-express-close]')
            || event.target === document.querySelector('[data-express-sheet]')
        ) {
            document.body.classList.remove('is-express-open');
            document.querySelector('[data-express-toggle]')?.setAttribute('aria-expanded', 'false');
        }
    });

    class CartStickyCheckout extends HTMLElement {
        connectedCallback() {
            this._observer = new IntersectionObserver((entries) => {
                this.classList.toggle('is-visible', !entries[0].isIntersecting);
            });
            this._observeStatic();

            // Each wholesale cart-drawer re-render (item added/qty changed) creates a fresh
            // anchor+group pair inside the new footer. The previous instance's group may still
            // be sitting in the (persistent, external) sheet from before the re-render -- drop
            // it so we don't end up with two sets of express-checkout buttons.
            const sheet = document.querySelector('[data-express-sheet]');
            const group = this.querySelector('[data-express-group]');
            if (sheet && group) {
                sheet.querySelectorAll('[data-express-group]').forEach((stale) => {
                    if (stale !== group) stale.remove();
                });
                currentExpressGroup = group;
            }

            if (!expressMediaQuery) {
                expressMediaQuery = window.matchMedia('(max-width: 449px)');
                expressMediaQuery.addEventListener('change', (e) => placeExpressGroup(e.matches));
            }
            placeExpressGroup(expressMediaQuery.matches);
        }

        // After the wholesale cart-drawer child replace, this element re-connects with a
        // fresh static [data-static-checkout] sibling. Scope the lookup to the enclosing
        // drawer so we always bind to the one static button (never the sticky copy, which
        // intentionally has no data-static-checkout). Retry on the next microtask if the
        // sibling hasn't connected yet within the same replaceChildren tick.
        _observeStatic() {
            const scope = this.closest('#cart-drawer') || document;
            const staticBtn = scope.querySelector('[data-static-checkout]');
            if (staticBtn) {
                this._observer.observe(staticBtn);
            } else if (!this._retried) {
                this._retried = true;
                Promise.resolve().then(() => {
                    if (this.isConnected) this._observeStatic();
                });
            }
        }

        disconnectedCallback() {
            this._observer?.disconnect();
        }
    }

    if (!window.customElements.get('cart-sticky-checkout')) {
        window.customElements.define('cart-sticky-checkout', CartStickyCheckout);
    }
})();
